<template>
  <header class="manager-navbar navbar">
    <div class="manager-navbar__group">
      <router-link :to="{ path: '/models' }">
        <svg
          class="manager-navbar__logo"
          viewBox="0 0 309 52"
          height="100%"
          width="100%"
          @click="$emit('logo-click')"
        >
          <use href="#simcision-logo-slim" height="10" />
        </svg>
        <!--<div class="manager-navbar__logo" @click="$emit('logo-click')" />-->
      </router-link>
      <slot></slot>
    </div>

    <div class="manager-navbar__group">
      <button
        class="manager-navbar__searchbutton mr-4"
        type="button"
        @click="searchVisible = true"
      >
        <lucide-icon name="search" />
        <span>{{ $t("actions.searchModel") }}...</span>

        <model-search-popup
          :visible="searchVisible"
          :y="40"
          @close="searchVisible = false"
        />
      </button>

      <user-avatar :user="user" class="mr-2" @click="openDropdown()" />
      <div
        v-show="isDropdownVisible"
        ref="dropdownmenu"
        class="manager-navbar__dropdown options-list"
      >
        <div class="options-list__header">
          <div v-if="user?.name">
            {{ user?.name }}
          </div>
          {{ user?.email }}
        </div>

        <router-link
          :to="{ name: 'settings-profile' }"
          class="options-list__option"
          @click="closeDropdown()"
        >
          {{ $t("labels.profile") }}
        </router-link>
        <router-link
          :to="{ name: 'settings-subscriptions' }"
          class="options-list__option"
          @click="closeDropdown()"
        >
          {{ $t("headings.subscriptions") }}
        </router-link>
        <router-link
          v-if="syncPossible"
          :to="{ name: 'settings-synchronization' }"
          class="options-list__option"
          @click="closeDropdown()"
        >
          {{ $t("labels.synchronization") }}
        </router-link>

        <template v-if="userOrganizations.length > 0">
          <hr />
          <h3 class="subtitle options-list__subtitle">
            {{ $t("headings.organizations") }}
          </h3>

          <router-link
            v-for="organization in userOrganizations"
            :key="organization.id"
            :to="{
              name: 'settings-organization',
              params: { organizationSlug: organization.slug }
            }"
            class="options-list__option"
          >
            <span>{{ organization.name }}</span>

            <span
              v-if="
                organization.membership.roleCode === 'OWNER' &&
                organization.membership?.role
              "
              :title="organization.membership?.role?.name"
            >
              ({{ organization.membership?.role?.name }})
            </span>
          </router-link>
        </template>

        <template v-if="user?.isUserAdmin || user?.isGlobalAdmin">
          <hr />
          <router-link
            :to="{ name: 'admin-users-list' }"
            class="options-list__option"
          >
            {{ $t("labels.userAdministration") }}
          </router-link>
        </template>

        <hr />

        <a
          class="options-list__option"
          href="https://support.simcision.com"
          target="_blank"
        >
          {{ $t("links.supportPage") }}
        </a>

        <hr />
        <router-link
          :to="{ name: 'login', query: { logout: 'true' } }"
          class="options-list__option"
          @click="closeDropdown()"
        >
          {{ $t("labels.logout") }}
        </router-link>
      </div>

      <locale-chooser />
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import envOptions from "@/helpers/Env";

import LocaleChooser from "@core/input/LocaleChooser.vue";
import UserAvatar from "@manager/common/UserAvatar.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ModelSearchPopup from "@manager/workspacemodels/ModelSearchPopup.vue";

import { useUser } from "@/api/user.store";
import { useOrganizations } from "@manager/organizations/organizations.store";

defineEmits<{
  (event: "logo-click"): void;
  (event: "edit-account"): void;
  (event: "edit-settings"): void;
  (event: "cloud-settings-updated"): void;
}>();

const { user } = useUser();
const { userOrganizations } = useOrganizations({}, { user });

const syncPossible = envOptions.synchronizeSaves;

const searchVisible = ref(false);
const isDropdownVisible = ref(false);
const dropdownmenu = useTemplateRef("dropdownmenu");

const boundCheckClose = (mouseEvent: MouseEvent) => {
  const menu = dropdownmenu.value;
  if (menu === null) {
    return;
  }
  if (mouseEvent.composedPath().includes(menu)) {
    return;
  }
  closeDropdown();
};

function openDropdown() {
  if (isDropdownVisible.value) {
    closeDropdown();
  } else {
    isDropdownVisible.value = true;

    setTimeout(() => {
      window.addEventListener("click", boundCheckClose);
    }, 5);
  }
}

function closeDropdown() {
  isDropdownVisible.value = false;
  window.removeEventListener("click", boundCheckClose);
}
</script>

<style lang="scss">
.manager-navbar {
  min-height: 5rem;
  box-shadow:
    inset 0 1rem 1rem -1rem rgba(0, 0, 0, 0.05),
    0 0 0.5rem rgba(0, 0, 0, 0.05);
  padding-right: 2rem;
  position: relative;

  &__group {
    display: flex;
    align-items: center;
  }

  &__usermenu {
    max-width: 20rem;
    margin-right: 0.5rem;
  }

  &__logo {
    position: relative;
    max-width: 100%;
    width: 12rem;
    height: 3rem;
    font-size: 1.5em;
    color: var(--color-action);
    margin-right: 2rem;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    left: auto;
    min-width: 15rem;
    width: max-content;
    max-width: 20rem;
  }

  &__searchbutton {
    margin-top: 1px;
    padding: 8px 1rem;
    height: 32px;
    display: flex;
    gap: 0.25rem;
    align-items: center;
    color: var(--color-text-medium);
    background-color: var(--color-background);
    border-radius: 5rem;
    border: 1px solid var(--color-border);
    transition:
      color 0.2s ease-in-out,
      box-shadow 0.2s ease-in-out;
    box-shadow: 0 0 0 var(--color-shadow-1);

    &:hover {
      color: var(--color-text);
      box-shadow: 0 0 0.5rem var(--color-shadow-1);
    }
  }
}
</style>
